// ============================================================================
// TRAINING REVIEW TAB
// Competence checklist for the Tier 2 facilitator course. Shows the ten
// criteria evaluated from real artefacts, with links to the evidence.
// Trainees submit; Tier 1 facilitators sign off.
// See .claude/plans/training-assessment-hook.md
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, CheckCircle2, XCircle, ExternalLink, Loader2,
  AlertTriangle, Lock, Send, RotateCcw,
} from 'lucide-react';
import {
  AssessmentResult, TrainingProgress, getAssessment, submitForReview,
  signOffTraining, listTrainees,
} from '../../services/trainingAssessment';

interface Props {
  projectId: string;
  /** Tier 1 facilitators (and system admins) can sign off. */
  canSignOff: boolean;
}

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  not_submitted: { label: 'Not submitted', className: 'bg-slate-100 text-slate-600 border-slate-200' },
  submitted: { label: 'Awaiting review', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  signed_off: { label: 'Signed off', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  returned: { label: 'Returned', className: 'bg-red-50 text-red-700 border-red-200' },
};

export const TrainingReviewTab: React.FC<Props> = ({ projectId, canSignOff }) => {
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState<AssessmentResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [showReturn, setShowReturn] = useState(false);
  const [trainees, setTrainees] = useState<TrainingProgress[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setAssessment(await getAssessment(projectId, selectedUserId));
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedUserId]);

  useEffect(() => { load(); }, [load]);

  // Reviewers pick which trainee they are assessing. Trainees never see this.
  useEffect(() => {
    if (!canSignOff) return;
    listTrainees(projectId)
      .then(setTrainees)
      .catch(() => { /* list is an aid, not a requirement */ });
  }, [canSignOff, projectId]);

  async function run(fn: () => Promise<AssessmentResult>) {
    setWorking(true);
    setError(null);
    try {
      setAssessment(await fn());
      setShowReturn(false);
      setNotes('');
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setWorking(false);
    }
  }

  if (loading) {
    return <div className="flex justify-center items-center py-20 text-slate-400 text-sm">Loading assessment…</div>;
  }

  if (!assessment) {
    return (
      <div className="p-8">
        <p className="text-sm text-red-600">{error || 'Assessment unavailable.'}</p>
      </div>
    );
  }

  const status = STATUS_STYLES[assessment.review.status] ?? STATUS_STYLES.not_submitted;
  const isSignedOff = assessment.review.status === 'signed_off';
  const pct = Math.round((assessment.metCount / assessment.totalCount) * 100);

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <GraduationCap size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Training Review</h1>
              <p className="text-slate-500 text-sm font-medium">
                Tier 2 facilitator competence, assessed from what you built in this sandbox
              </p>
            </div>
          </div>
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${status.className}`}>
            {status.label}
          </span>
        </header>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 flex items-center gap-3">
            <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}

        {canSignOff && trainees.length > 0 && (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5">
            <label className="block text-sm font-semibold text-navy mb-2">Trainee</label>
            <select
              value={selectedUserId ?? ''}
              onChange={e => setSelectedUserId(e.target.value || undefined)}
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
            >
              <option value="">My own assessment</option>
              {trainees.map(t => (
                <option key={t.userId} value={t.userId}>
                  {t.userDisplayName ?? t.userEmail ?? t.userId} — {STATUS_STYLES[t.review.status]?.label ?? t.review.status}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Progress */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-navy">
              {assessment.metCount} of {assessment.totalCount} criteria met
            </p>
            <span className="text-sm text-slate-500">{pct}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${assessment.allMet ? 'bg-emerald-500' : 'bg-primary'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          {assessment.fromSnapshot && (
            <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5">
              <Lock size={12} /> Frozen at sign-off. Sandbox resets do not affect this record.
            </p>
          )}
        </div>

        {/* Reviewer notes */}
        {assessment.review.notes && (
          <div className={`rounded-2xl border p-5 ${isSignedOff ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
            <p className="text-xs font-bold uppercase tracking-wider mb-1.5 text-slate-600">
              Reviewer notes
              {assessment.review.reviewerName ? ` — ${assessment.review.reviewerName}` : ''}
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">{assessment.review.notes}</p>
          </div>
        )}

        {/* Criteria */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <ul className="divide-y divide-[#e2e8f0]">
            {assessment.criteria.map((c, i) => (
              <li key={c.id} className="p-5 flex gap-4">
                <span className="flex-shrink-0 mt-0.5">
                  {c.status === 'met'
                    ? <CheckCircle2 size={20} className="text-emerald-500" />
                    : <XCircle size={20} className="text-slate-300" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400">{i + 1}</span>
                    <p className={`text-sm font-semibold ${c.status === 'met' ? 'text-navy' : 'text-slate-500'}`}>
                      {c.label}
                    </p>
                  </div>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">{c.detail}</p>
                  {c.evidence.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {c.evidence.map(e => (
                        <button
                          key={`${c.id}-${e.id}`}
                          onClick={() => navigate(e.href)}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-dark transition-colors"
                        >
                          <ExternalLink size={12} /> {e.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {c.quality && (
                    <p className="text-xs text-slate-400 mt-2">
                      Quality {c.quality.score}/5 — {c.quality.rationale}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        {!isSignedOff && (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5 flex flex-col gap-4">
            {!canSignOff && (
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm text-slate-500">
                  {assessment.allMet
                    ? 'All criteria met. Submit for a Tier 1 facilitator to review.'
                    : 'Complete the remaining criteria before submitting.'}
                </p>
                <button
                  onClick={() => run(() => submitForReview(projectId))}
                  disabled={!assessment.allMet || working || assessment.review.status === 'submitted'}
                  className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-50"
                >
                  {working ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {assessment.review.status === 'submitted' ? 'Submitted' : 'Submit for review'}
                </button>
              </div>
            )}

            {canSignOff && (
              <>
                <div className="flex items-center justify-between gap-4">
                  <p className="text-sm text-slate-500">
                    {assessment.allMet
                      ? 'All criteria met. Signing off freezes this record.'
                      : `${assessment.totalCount - assessment.metCount} criteria unmet. Return with notes, or override with a reason.`}
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowReturn(v => !v)}
                      className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
                    >
                      <RotateCcw size={14} /> Return with notes
                    </button>
                    <button
                      onClick={() => run(() => signOffTraining(
                        projectId, assessment.userId, 'passed', notes || undefined, !assessment.allMet
                      ))}
                      disabled={working || (!assessment.allMet && !notes.trim())}
                      className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-emerald-600 hover:bg-emerald-700 transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-50"
                    >
                      {working ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                      Sign off
                    </button>
                  </div>
                </div>

                {(showReturn || !assessment.allMet) && (
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">
                      Notes {!assessment.allMet && <span className="text-red-500">*</span>}
                    </label>
                    <textarea
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                      rows={3}
                      placeholder="What the trainee needs to do, or why you are overriding an unmet criterion"
                      className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                    />
                    {showReturn && (
                      <button
                        onClick={() => run(() => signOffTraining(projectId, assessment.userId, 'returned', notes))}
                        disabled={working || !notes.trim()}
                        className="mt-3 flex items-center justify-center gap-2 rounded-xl h-10 px-4 bg-amber-600 hover:bg-amber-700 transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-50"
                      >
                        <RotateCcw size={14} /> Return to trainee
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {isSignedOff && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 flex items-center gap-3">
            <CheckCircle2 size={20} className="text-emerald-600 flex-shrink-0" />
            <p className="text-sm text-emerald-800">
              Signed off{assessment.review.reviewerName ? ` by ${assessment.review.reviewerName}` : ''}
              {assessment.review.reviewedAt ? ` on ${new Date(assessment.review.reviewedAt).toLocaleDateString('en-GB')}` : ''}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingReviewTab;
